import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { WALK, FLOW, HERO } from '../content/narrative';
import { REQUEST_ACCESS_URL } from '../config/links';

/* The homepage walk: the DRV-400 drive module of RBT-100 Warehouse robot,
   from a sentence through a bench prototype, Rev A and its failures, to a
   Rev B validated on a working warehouse floor — and the lesson reaching the
   next team.

   Desktop (≥1024px, motion allowed): beats scroll on the left; a sticky
   product screen on the right swaps to whichever beat part sits at the middle
   of the viewport. Phones and reduced motion: nothing pinned — each beat is a
   card with its own static screen. Screens are HTML so every number is real,
   selectable, crawlable text. There is deliberately no 3D model panel.

   Product language on screen: ✓ met (green, only when evidence backs it),
   ! open (amber), · not yet (grey, dashed). Anything MIL drafted is dashed
   with a Proposed badge until you approve it. Engine numbers carry a
   "computed" tag. No chat bubbles anywhere. */

const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, Consolas, monospace";
const hand = "'Kalam', 'Caveat', cursive";
const GREEN = 'var(--wb-green)';
const AMBER = 'var(--wb-amber)';

const usePinned = () => {
  /* Pin only where the screen fits: wide enough for two columns, tall enough
     for the longest beat's screen plus a readable model panel. */
  const q = '(min-width: 1024px) and (min-height: 760px) and (prefers-reduced-motion: no-preference)';
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return undefined;
    const f = () => setOn(m.matches);
    f();
    m.addEventListener('change', f);
    return () => m.removeEventListener('change', f);
  }, []);
  return on;
};

/* ── small product-language atoms ─────────────────────────────────────── */
const Id = ({ children }) => <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', fontWeight: 700, whiteSpace: 'nowrap' }}>{children}</span>;
const Tag = ({ children, tone = 'muted', nowrap = false }) => {
  const c = { muted: 'var(--wb-muted)', green: GREEN, amber: AMBER, orange: 'var(--wb-orange)' }[tone];
  return <span style={{ display: 'inline-block', fontFamily: mono, fontSize: 'var(--t-sm)', color: c, border: `1px solid ${c}`, borderRadius: 999, padding: '0 7px', lineHeight: 1.5, maxWidth: '100%', whiteSpace: nowrap ? 'nowrap' : 'normal' }}>{children}</span>;
};
const Computed = () => <Tag>computed</Tag>;
const Proposed = () => <Tag tone="orange">Proposed</Tag>;
const Status = ({ s }) => {
  if (s === 'met') return <span style={{ color: GREEN, fontFamily: mono, fontWeight: 800 }} aria-label="met">✓</span>;
  if (s === 'open') return <span style={{ color: AMBER, fontFamily: mono, fontWeight: 800 }} aria-label="open">!</span>;
  if (s === 'na') return <span style={{ color: 'var(--wb-muted)', fontFamily: mono }} aria-label="does not apply">–</span>;
  return <span style={{ color: 'var(--wb-muted)', fontFamily: mono }} aria-label="not yet">·</span>;
};
const Card = ({ proposed, children, style }) => (
  <div style={{ border: `1px ${proposed ? 'dashed var(--wb-orange)' : 'solid var(--wb-line)'}`, borderRadius: 10, padding: '10px 12px', background: proposed ? 'rgba(185,79,30,0.04)' : 'var(--wb-paper)', ...style }}>
    {children}
  </div>
);
/* Rows flow as text (not flex items), so a long sentence wraps in place
   instead of dropping to its own line and stranding the ID or ✓ before it. */
const Row = ({ children, style }) => (
  <div className="walk-row" style={{ fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', lineHeight: 1.6, overflowWrap: 'anywhere', ...style }}>{children}</div>
);
const Label = ({ children }) => (
  <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>{children}</div>
);
const Btn = ({ children, primary }) => (
  <span aria-hidden style={{ whiteSpace: 'nowrap', fontFamily: sans, fontSize: 'var(--t-sm)', fontWeight: 700, padding: '5px 12px', borderRadius: 999, border: '1px solid var(--wb-ink)', background: primary ? 'var(--wb-ink)' : 'transparent', color: primary ? 'var(--wb-on-ink)' : 'var(--wb-ink)' }}>{children}</span>
);

/* ── the seven-step strip ─────────────────────────────────────────────── */
const Strip = ({ strip }) => (
  <div className="flex flex-wrap gap-1" aria-label="Seven-step cycle">
    {FLOW.steps.map((st, i) => {
      const s = strip[i];
      const border = s === 'met' ? `1px solid ${GREEN}` : s === 'open' ? `1px solid ${AMBER}` : '1px dashed var(--wb-line)';
      const color = s === 'met' ? GREEN : s === 'open' ? AMBER : 'var(--wb-muted)';
      return (
        <span key={st.id} className="flex items-center gap-1" style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color, border, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
          <Status s={s} /> {st.name}
        </span>
      );
    })}
  </div>
);

/* Compact strip for the carousel: seven pips and a one-line tally, instead
   of seven labelled chips that wrap to three rows on a phone. */
const PipStrip = ({ strip }) => {
  const met = strip.filter((x) => x === 'met').length;
  const open = strip.filter((x) => x === 'open').length;
  const openNames = FLOW.steps.filter((_, i) => strip[i] === 'open').map((st) => st.name).join(', ');
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" aria-label={`Seven-step cycle: ${met} met${open ? `, ${open} open (${openNames})` : ''}`}>
      <span className="flex gap-[3px]" aria-hidden>
        {FLOW.steps.map((st, i) => {
          const x = strip[i];
          const c = x === 'met' ? GREEN : x === 'open' ? AMBER : null;
          return <span key={st.id} title={st.name} style={{ width: 12, height: 12, borderRadius: 3, background: c || 'transparent', border: `1px ${c ? 'solid' : 'dashed'} ${c || 'var(--wb-line)'}` }} />;
        })}
      </span>
      <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)' }}>
        <span style={{ color: GREEN }}>✓ {met} met</span>
        {open > 0 && <span style={{ color: AMBER }}> · ! {openNames}</span>}
      </span>
    </div>
  );
};

/* ── what the product screen shows on each beat ───────────────────────── */
/* Compact screens (the mobile carousel): each screen keeps its key card and
   tucks secondary cards behind a tap, so a beat stays near one screen tall. */
const CompactCtx = createContext(false);
const More = ({ label, children }) => {
  const compact = useContext(CompactCtx);
  const [open, setOpen] = useState(false);
  if (!compact) return children;
  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 text-left"
        style={{ padding: '8px 10px', border: '1px dashed var(--wb-line)', borderRadius: 10, background: 'transparent', cursor: 'pointer', fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)' }}
      >
        <span>{label}</span><span aria-hidden style={{ color: 'var(--wb-orange)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && children}
    </div>
  );
};

const PLAN = [
  ['Draft the requirements', 'MIL'], ['Approve the requirements', 'You'], ['Surface what the team already knows', 'MIL'],
  ['Draft the drive and its geometry', 'MIL'], ['Check heat, power and stopping', 'Engine'], ['Build a prototype and measure it on the wheel dyno', 'You'],
  ['Predict gear life and backlash', 'Engine'], ['Draft the life, wash-down and fleet tests', 'MIL'], ['Release Rev A', 'You · sign-in'],
];
/* Each drive requirement traces up to the robot requirement (or the sibling
   assembly budget) it serves — flow-down across levels, not just within one. */
const REQS = [
  ['REQ-SPD-001', 'Drive at 1.5 m/s with a 300 kg payload.', null, 'SYS-003', 'move a 300 kg payload at 1.5 m/s'],
  ['REQ-POS-002', 'Hold wheel position within 0.5° under load.', 'Revised by you; was 0.1°.', 'SYS-007', 'dock to a charger or conveyor within ±5 mm'],
  ['REQ-LIF-003', 'Three years without a gearbox change: 12,000 driving hours.', 'Added by you; replaces a service every six months.', 'SYS-011', 'three years in service between planned maintenance'],
  ['REQ-ENV-004', '0 to 45 °C, nightly floor-scrubber wash-down, crossing floor joints and dock plates up to 15 mm.', null, 'SYS-009', 'operate in warehouses at 0 to 45 °C, washed nightly'],
  ['REQ-PWR-005', '180 W or less per module at cruise.', null, 'PWR-300', 'power budget: 360 W for both drives'],
  ['REQ-STP-006', 'Stop from 1.5 m/s within 1.0 m, fully loaded.', null, 'SYS-005', 'stop within 1.0 m when NAV-600 detects a person'],
];
const reqCard = ([id, text, note, parent, parentText]) => (
  <Card key={id} style={{ padding: '7px 10px' }}>
    <Row><Id>{id}</Id><span>{text}</span></Row>
    {note && <Row style={{ color: 'var(--wb-orange)' }}>{note}</Row>}
    <Row style={{ color: 'var(--wb-muted)' }}><span>↑</span><Id>{parent}</Id><span>{parentText}</span></Row>
  </Card>
);
const Num = ({ children }) => <span style={{ fontFamily: mono }}>{children}</span>;

const BeatBody = ({ i, sub }) => {
  const compact = useContext(CompactCtx);
  switch (i) {
    case 0:
      return (
        <>
          {!compact && <Card>
            <Label>Thread brief</Label>
            <Row><b>Purpose</b> Move a 300 kg payload at 1.5 m/s, two shifts a day, for three years without a gearbox change.</Row>
          </Card>}
          <Card proposed style={{ marginTop: compact ? 0 : 8 }}>
            <div className="flex items-center justify-between gap-2" style={{ marginBottom: 6 }}><Label>MIL’s plan</Label><Proposed /></div>
            <ol className="list-none p-0 m-0" style={{ display: 'grid', gap: 3 }}>
              {PLAN.map(([t, who], n) => (
                <li key={t} className="flex items-baseline justify-between gap-2" style={{ fontFamily: sans, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)' }}>
                  <span><span style={{ fontFamily: mono, color: 'var(--wb-muted)' }}>{n + 1}.</span> {t}</span>
                  <Tag nowrap tone={who === 'MIL' ? 'orange' : who === 'Engine' ? 'muted' : 'green'}>{who}</Tag>
                </li>
              ))}
            </ol>
            <div className="flex gap-2" style={{ marginTop: 10 }}><Btn primary>Accept plan</Btn><Btn>Edit</Btn><Btn>Stop</Btn></div>
          </Card>
        </>
      );
    case 1:
      return (
        <div style={{ display: 'grid', gap: 5 }}>
          {(compact ? REQS.slice(0, 3) : REQS).map(reqCard)}
          {compact
            ? <More label="Show all 6 requirements"><div style={{ display: 'grid', gap: 5, marginTop: 5 }}>{REQS.slice(3).map(reqCard)}</div></More>
            : <Row style={{ color: 'var(--wb-muted)' }}>Each: owner, proven by test, approved by you.</Row>}
        </div>
      );
    case 2:
      return (
        <>
          <Card>
            <Label>Lesson surfaced</Label>
            <Row><Id>L-04</Id><span>Put the encoder on the wheel shaft, not the motor. Gearbox backlash hid 2% odometry drift.</span></Row>
            <Row style={{ color: 'var(--wb-muted)', marginTop: 4 }}>From: pallet mover, 2024 · Applies to: <Id>ENC-450</Id>, <Id>DRV-400</Id></Row>
          </Card>
          {!compact && <Card style={{ marginTop: 8 }}>
            <Row><b>Measured impact torque at a robot wheel crossing floor joints:</b> looked, 0 found.</Row>
            <Row><b>Smooth-floor estimate:</b> <Num>25 N·m</Num> peak (acceleration and stops).</Row>
          </Card>}
          <Card style={{ marginTop: 8 }}>
            <div className="flex items-center justify-between gap-2"><Label>Study proposed</Label><Tag tone="amber">Deferred by you, Day 2 · risk open</Tag></div>
            <Row><Id>STD-011</Id><span>Impact torque at a loaded wheel over floor joints.</span></Row>
            <Row style={{ color: 'var(--wb-muted)' }}>Method: instrument the pallet mover (surrogate), 1 week · Traces to <Id>REQ-LIF-003</Id>, <Id>REQ-ENV-004</Id></Row>
          </Card>
        </>
      );
    case 3: {
      const revised = sub >= 1;
      return (
        <>
          <Card proposed={sub < 2}>
            <div className="flex items-center justify-between gap-2"><Label>Heat check · DRIVE-THERMAL v2</Label>{sub < 2 ? <Proposed /> : <Tag tone="green">Applied by you</Tag>}</div>
            <Row>
              <b>Motor winding:</b>
              {revised ? <span>138 °C → 104 °C, limit 120 °C</span> : <span style={{ color: 'var(--wb-red)' }}>138 °C, limit 120 °C · over</span>}
              <Computed />
            </Row>
            {revised && <Row style={{ color: 'var(--wb-muted)' }}>Controller <Id>CTL-460</Id> moved to a finned pocket; thermal pad to the chassis.</Row>}
          </Card>
          <Card style={{ marginTop: 8 }}>
            <Row><Id>D-006</Id><span>Spur gears (simple, no axial load).</span></Row>
            <Row style={{ color: 'var(--wb-muted)' }}>Rejected: helical gears (quieter and stronger, but they need a thrust bearing and better sealing to keep grease in).</Row>
          </Card>
        </>
      );
    }
    case 4:
      return (
        <Card>
          <div className="flex items-center justify-between gap-2"><Label>Inputs</Label><Id>TR-301 · wheel dyno</Id></div>
          {[
            ['met', 'Drag torque', '0.9 N·m', 'measured'],
            ['met', 'Efficiency', '82%', 'measured'],
            ['met', 'Encoder accuracy', '0.05°', 'measured'],
            ['met', 'Encoder counts at full current', '4% corrupted', 'fixed: differential signalling on a shielded cable, 0 errors'],
            ['open', 'Floor impact torque', '25 N·m', 'assumed · STD-011 deferred · check on the life-test rig'],
          ].map(([st, k, v, how]) => (
            <Row key={k} style={{ padding: '4px 0', borderBottom: '1px dashed var(--wb-line)' }}>
              <Status s={st} /><b>{k}:</b><Num>{v}</Num><span style={{ color: st === 'open' ? AMBER : 'var(--wb-muted)' }}>{how}</span>
            </Row>
          ))}
        </Card>
      );
    case 5:
      return (
        <>
          <Card>
            <Row><b>Gear life:</b><Num>15,000 h</Num><span style={{ color: 'var(--wb-muted)' }}>(required 12,000)</span><Computed /></Row>
            <Row style={{ color: 'var(--wb-muted)' }}>Method: <Id>GEAR-WEAR v1</Id> <Tag tone="amber">uncalibrated here</Tag></Row>
            <Row style={{ color: 'var(--wb-muted)', marginTop: 2 }}>Rests on: drag 0.9 N·m (measured), efficiency 82% (measured), floor impact 25 N·m <span style={{ color: AMBER }}>(assumed)</span></Row>
          </Card>
          <Card style={{ marginTop: 8 }}>
            <Row><b>Backlash</b><Num>0.2°</Num><span>·</span><b>Power</b><Num>150 W</Num><span>per module</span><Tag>predicted</Tag></Row>
            <Row style={{ marginTop: 4 }}><Id>Rev A</Id><span>released. Signed by you, Day 12.</span></Row>
          </Card>
        </>
      );
    case 6:
      return (
        <Card>
          <Row><Id>TR-310</Id><span>Life test</span></Row>
          <Row style={{ color: 'var(--wb-muted)' }}>Pass line set Day 10, before the run. 900 h under 300 kg, replaying a recorded warehouse floor.</Row>
          <Row style={{ marginTop: 8 }}><Status s="open" /><b>Backlash</b><Num>0.9° at 900 h</Num><span style={{ color: AMBER }}>Limit 0.5°: not met.</span></Row>
          <Row><span style={{ color: 'var(--wb-muted)' }}>Torque log:</span><span>impact peaks <Num>95 N·m</Num>.</span></Row>
          {sub >= 1 && <Row style={{ marginTop: 4 }}><b>Teardown:</b><span><Id>GR-421</Id> first-stage pinion (in <Id>GBX-420</Id>) pitted.</span></Row>}
        </Card>
      );
    case 7:
      return (
        <>
          <Card>
            <Label>Failure Thread</Label>
            <Row><Id>ISS-44</Id><span>Water at the encoder after 900 h.</span><Id>TR-311</Id></Row>
            <Row style={{ color: 'var(--wb-muted)', marginTop: 4 }}>Touches: <Id>SEL-470</Id>, <Id>ENC-450</Id>, <Id>REQ-ENV-004</Id></Row>
          </Card>
          <Card style={{ marginTop: 8 }}>
            <Label>Similar failure</Label>
            <Row><Id>ISS-12</Id><span>Pallet mover, 2024. Shaft seal worn by floor grit after 5 months of nightly scrubbing. Fixed with a V-ring excluder and a labyrinth.</span></Row>
          </Card>
        </>
      );
    case 8:
      return (
        <>
          <Card>
            <Row><b>Predicted</b><Num>15,000 h</Num><b>failed at</b><Num>900 h</Num><span style={{ color: AMBER }}>outside ±20%</span></Row>
            <Row style={{ marginTop: 4 }}><b>Explanation:</b> floor impact was assumed at 25 N·m, with <Id>STD-011</Id> deferred on Day 2; the rig measured peaks of 95 N·m.</Row>
            <Row><b>Recomputed</b> with the measured spectrum: <Num>1,050 h</Num><span>17% from what happened: inside.</span><Computed /></Row>
            <Row style={{ marginTop: 4 }}><Tag tone="green">Approved by you</Tag></Row>
          </Card>
          <More label="Written back · 4 records"><Card style={{ marginTop: 8 }}>
            <Label>Written back</Label>
            <ul className="list-none p-0 m-0" style={{ display: 'grid', gap: 3 }}>
              <li><Row>→ The measured floor-shock spectrum becomes an input for the next analysis.</Row></li>
              <li><Row>→ <Id>STD-011</Id> closes with the rig’s measured spectrum.</Row></li>
              <li><Row>→ <Id>GEAR-WEAR v1</Id> has its first correlation, 1 of the 3 it needs before it is trusted.</Row></li>
              <li><Row>→ <Id>L-11</Id> A loaded wheel crossing floor joints sees impact torque about four times the smooth-floor design peak. Take the shock off the gears, or size them on the measured spectrum. Applies to every wheeled drive.</Row></li>
            </ul>
          </Card></More>
        </>
      );
    case 9:
      return (
        <>
          <More label="Carried from Rev A · 5 records"><Card style={{ marginBottom: 8 }}>
            <Label>Carried from Rev A</Label>
            <Row><Id>L-11</Id> (compliant mount) · <Id>D-006</Id>’s rejected option returns (helical gears) · <Id>ISS-12</Id>’s fix (labyrinth and excluder) · the prototype’s differential encoder · the measured floor-shock spectrum</Row>
          </Card></More>
          <Card proposed={false} style={{ marginTop: 8, opacity: sub >= 1 ? 1 : 0.55, transition: 'opacity .3s' }}>
            <Label>Interface change · ripples up</Label>
            <Row><Id>DRV-400</Id><span>↔</span><Id>CHS-200</Id><span>mounting: 4 × M8 → 6 × M8 on the <Id>MNT-490</Id> plate.</span></Row>
            <Row style={{ color: 'var(--wb-muted)' }}>Thread opened on <Id>CHS-200</Id> for the chassis owner.{sub >= 1 && <span> <Tag tone="green">Approved by the chassis owner, Day 60</Tag></span>}</Row>
          </Card>
          <Card style={{ marginTop: 8 }}>
            <Row><b>Peak gear torque</b><Num>30 N·m</Num>·<b>Gear life</b><Num>45,000 h</Num>·<b>Backlash</b><Num>0.3°</Num>·<b>Power</b><Num>160 W</Num><Computed /></Row>
            {sub >= 1 && <Row style={{ marginTop: 4 }}><Id>Rev B</Id><span>released. Signed by you, Day 61.</span></Row>}
          </Card>
        </>
      );
    case 10:
      return (
        <>
          <Card>
            <Row><Id>TR-320</Id><span>On the warehouse floor</span></Row>
            <Row style={{ marginTop: 4 }}><Status s="met" /><span>1,000 h at two shifts a day. Backlash <Num>0.3°</Num>. Dry after nightly wash-downs. <Num>0</Num> encoder errors.</span></Row>
            <Row><Status s="met" /><span>Peak gear torque <Num>32 N·m</Num> (predicted 30): inside.</span></Row>
            <Row style={{ marginTop: 4 }}><Tag tone="green">Rev B · validated: released and field-tested</Tag></Row>
          </Card>
          <Card style={{ marginTop: 8, opacity: sub >= 1 ? 1 : 0.55, transition: 'opacity .3s' }}>
            <Label>Day 150 · another team · RBT-200 pallet robot drive</Label>
            <Row><Id>L-11</Id><span>applied: compliant mount, gears sized on measured floor shock.</span></Row>
          </Card>
        </>
      );
    default:
      return null;
  }
};

/* Hand-written margin note with a short hand-drawn arrow into the screen. */
const MarginNote = ({ text }) => (
  <div className="flex items-start gap-1" style={{ fontFamily: hand, fontSize: 'var(--t-md)', color: 'var(--wb-orange)', lineHeight: 1.3 }}>
    <svg width="30" height="22" viewBox="0 0 30 22" aria-hidden style={{ flex: '0 0 auto', marginTop: 4 }}>
      <path d="M3 3 C 10 16, 18 18, 26 15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M21 11 L 26 15 L 20 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    <span>{text}</span>
  </div>
);

const Screen = ({ i, sub }) => {
  const beat = WALK.beats[i];
  const compact = useContext(CompactCtx);
  const crumbs = compact ? WALK.crumbs.map((c) => c.split(' ')[0]) : WALK.crumbs;
  return (
    <div style={{ background: 'var(--wb-paper)', border: '1px solid var(--wb-line)', borderRadius: 14, overflow: 'hidden', boxShadow: '0 12px 28px var(--wb-shadow)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3" style={{ padding: '9px 14px', background: 'var(--wb-soft)', borderBottom: '1px solid var(--wb-line)' }}>
        {/* Breadcrumb: the drive is a sub-assembly of a larger, equally driven system. */}
        <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)' }}>
          {crumbs.map((c, n) => (
            <React.Fragment key={c}>
              {n > 0 && <span aria-hidden> › </span>}
              <span style={{ color: n === crumbs.length - 1 ? 'var(--wb-ink)' : 'var(--wb-muted)', fontWeight: n === crumbs.length - 1 ? 800 : 400 }}>{c}</span>
            </React.Fragment>
          ))}
        </span>
        <span style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 700 }}>Day {beat.day} · {beat.step}</span>
      </div>
      <div style={{ padding: compact ? '8px 12px' : '10px 14px', borderBottom: '1px solid var(--wb-line)' }}>{compact ? <PipStrip strip={beat.strip} /> : <Strip strip={beat.strip} />}</div>
      <div className="walk-screen-body" key={i} style={{ padding: compact ? 10 : 12 }}>
        <BeatBody i={i} sub={sub} />
      </div>
    </div>
  );
};

/* ── the section ──────────────────────────────────────────────────────── */
/* ── the phone walk: a swipe carousel ─────────────────────────────────────
   One beat per slide, native scroll-snap (swipes like a photo gallery), with
   ‹ › buttons and a progress bar for keyboards and screen readers. The next
   slide peeks in at the edge so the swipe is discoverable. The track takes
   the active slide's height, so short beats don't leave a gap. Screens are
   compact and show each beat's final state. */
const Carousel = ({ beatText }) => {
  const trackRef = useRef(null);
  const slideRefs = useRef([]);
  const [idx, setIdx] = useState(0);
  const [h, setH] = useState(null);
  const n = WALK.beats.length;
  const reduce = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const go = (k) => {
    const t = trackRef.current; const el = slideRefs.current[k];
    if (!t || !el) return;
    t.scrollTo({ left: el.offsetLeft - t.offsetLeft, behavior: reduce() ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    const t = trackRef.current; if (!t) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const left = t.scrollLeft;
        let best = 0; let bestD = Infinity;
        slideRefs.current.forEach((el, k) => { const d = Math.abs(el.offsetLeft - t.offsetLeft - left); if (d < bestD) { bestD = d; best = k; } });
        setIdx(best);
      });
    };
    t.addEventListener('scroll', onScroll, { passive: true });
    return () => { t.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  /* Track height follows the active slide, including when a "More" opens. */
  useEffect(() => {
    const el = slideRefs.current[idx]; if (!el) return undefined;
    const fit = () => setH(el.offsetHeight);
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, [idx]);

  const b = WALK.beats[idx];
  const btn = (disabled) => ({
    width: 40, height: 40, borderRadius: 999, border: '1px solid var(--wb-ink)', background: disabled ? 'transparent' : 'var(--wb-ink)',
    color: disabled ? 'var(--wb-line)' : 'var(--wb-on-ink)', borderColor: disabled ? 'var(--wb-line)' : 'var(--wb-ink)', fontSize: 'var(--t-md)', cursor: disabled ? 'default' : 'pointer', flex: '0 0 auto',
  });

  return (
    <div className="mt-8 mx-auto" style={{ maxWidth: 720 }} role="region" aria-roledescription="carousel" aria-label="The walk, one beat at a time">
      {/* progress + controls */}
      <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
        <button type="button" aria-label="Previous beat" disabled={idx === 0} onClick={() => go(idx - 1)} style={btn(idx === 0)}>‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div aria-live="polite" style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-ink)', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {idx + 1} / {n} · Day {b.day} · {b.step}
          </div>
          <div className="flex gap-[3px]" style={{ marginTop: 6 }} aria-hidden>
            {WALK.beats.map((_, k) => (
              <span key={k} style={{ flex: 1, height: 3, borderRadius: 2, background: k <= idx ? 'var(--wb-orange)' : 'var(--wb-line)' }} />
            ))}
          </div>
        </div>
        <button type="button" aria-label="Next beat" disabled={idx === n - 1} onClick={() => go(idx + 1)} style={btn(idx === n - 1)}>›</button>
      </div>

      <CompactCtx.Provider value>
        <ol
          ref={trackRef}
          className="walk-track list-none p-0 m-0"
          style={{ height: h ?? 'auto' }}
        >
          {WALK.beats.map((beat, i) => (
            <li
              key={i}
              data-beat={i}
              ref={(el) => { slideRefs.current[i] = el; }}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${n}`}
              /* Off-screen slides are inert: out of the tab order and hidden from
                 screen readers, so focus never lands in a slide you can't see. */
              inert={i !== idx ? '' : undefined}
              className="walk-slide"
              style={{ border: '1px solid var(--wb-line)', borderRadius: 16, padding: 16, background: 'var(--wb-paper-a)' }}
            >
              {beatText(beat, i)}
              <div style={{ marginTop: 14 }}><Screen i={i} sub={beat.body.length - 1} /></div>
              <div style={{ marginTop: 10 }}><MarginNote text={beat.note} /></div>
            </li>
          ))}
        </ol>
      </CompactCtx.Provider>
      {idx === 0 && <p style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-muted)', margin: '10px 0 0', textAlign: 'center' }}>Swipe for the next beat →</p>}
    </div>
  );
};

const Walk = () => {
  const pinned = usePinned();
  const [active, setActive] = useState({ i: 0, sub: 0 });
  const listRef = useRef(null);

  /* Active beat = the last beat part whose top has passed the middle of the
     viewport. Computed from scroll position (not crossing events), so jumps —
     anchor links, Home/End, a restored scroll — never leave a stale screen. */
  useEffect(() => {
    if (!pinned) return undefined;
    const els = [...(listRef.current?.querySelectorAll('[data-walk]') || [])];
    let raf = 0;
    const update = () => {
      raf = 0;
      const mid = window.innerHeight / 2;
      let pick = els[0];
      for (const el of els) if (el.getBoundingClientRect().top <= mid) pick = el;
      if (!pick) return;
      const [i, sub] = pick.dataset.walk.split('-').map(Number);
      setActive((cur) => (cur.i === i && cur.sub === sub ? cur : { i, sub }));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [pinned]);

  const header = (
    <div className="max-w-[860px]">
      <div className="wb-kicker">{WALK.eyebrow}</div>
      <h2 className="wb-display" style={{ fontSize: 'var(--t-lg)', margin: '14px 0 16px', lineHeight: 1.05 }}>{WALK.title}</h2>
      <p style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', lineHeight: 1.6, margin: 0, maxWidth: 640 }}>{WALK.intro}</p>
    </div>
  );

  /* A render function, not a component: a component defined in here would
     remount on every beat change and drop the nodes the observer watches. */
  const beatText = (b, i) => (
    <>
      <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Day {b.day} · {b.step}
      </div>
      <h3 className="wb-display" style={{ fontSize: 'var(--t-lg)', lineHeight: 1.08, margin: '10px 0 14px' }}>{b.headline}</h3>
      {b.body.map((para, sub) => (
        <p
          key={sub}
          data-walk={pinned ? `${i}-${sub}` : undefined}
          style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', lineHeight: 1.65, margin: sub ? '12px 0 0' : 0, maxWidth: 560 }}
        >
          {para}
        </p>
      ))}
    </>
  );

  return (
    <section id="walk" className="px-5 py-20 md:py-[104px]" style={{ borderTop: '1px solid var(--wb-line)' }} aria-labelledby="walk-title">
      <div className="max-w-[1180px] mx-auto">
        <a className="walk-skip" href="#why">Skip the walk ↓</a>
        <div id="walk-title">{header}</div>

        {pinned ? (
          <div className="grid grid-cols-[1fr_1.12fr] gap-12 mt-6" ref={listRef}>
            <ol className="list-none p-0 m-0">
              {WALK.beats.map((b, i) => (
                <li key={i} data-beat={i} data-walk={`${i}-0`} style={{ minHeight: '82vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '8vh 0' }}>
                  {/* A paper card under the beat copy, so it reads cleanly over the
                      graph-paper grid, matching the product screen beside it. */}
                  <div style={{ background: 'var(--wb-paper)', border: '1px solid var(--wb-line)', borderRadius: 16, padding: '26px 28px', boxShadow: '0 8px 22px var(--wb-shadow-soft)' }}>
                    {beatText(b, i)}
                  </div>
                </li>
              ))}
            </ol>
            <div>
              <div style={{ position: 'sticky', top: 84 }}>
                <div style={{ marginBottom: 8, minHeight: 34 }}><MarginNote text={WALK.beats[active.i].note} key={active.i} /></div>
                <Screen i={active.i} sub={active.sub} />
              </div>
            </div>
          </div>
        ) : (
          <Carousel beatText={beatText} />
        )}

        {/* The payoff, earned by the walk above. */}
        <p className="wb-display" style={{ fontSize: 'var(--t-lg)', fontStyle: 'italic', color: 'var(--wb-orange)', lineHeight: 1.12, maxWidth: '26ch', margin: '64px 0 0' }}>
          {HERO.supporting}
        </p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a className="wb-btn" href={REQUEST_ACCESS_URL}>{HERO.ctaPrimary}</a>
          {HERO.watchUrl && <a className="wb-btn ghost" href={HERO.watchUrl}>▶ Watch the loop</a>}
        </div>
      </div>
    </section>
  );
};

export default Walk;
